import type { PaletteColor } from "@trip/domain/palette";
import type { Poi } from "../lib/pois.ts";
import { Avatar } from "../ui/Avatar.tsx";

/** How many faces the stack shows before the rest become a "+N" chip. */
export const MAX_FACES = 3;

/** How many people the sentence names before the rest become "and N others": the faces' count. */
const MAX_NAMED = MAX_FACES;

/**
 * Names in a sentence: "Ana", "Ana and Ben", "Ana, Ben and Caro"; past `MAX_NAMED` the first three
 * and a count, "Ana, Ben, Caro and 17 others", so that twenty voters cannot grow the line they sit on.
 */
function listSaid(names: string[]): string {
  if (names.length > MAX_NAMED) {
    const rest = names.length - MAX_NAMED;
    return `${names.slice(0, MAX_NAMED).join(", ")} and ${rest} ${rest === 1 ? "other" : "others"}`;
  }
  return names.length < 2
    ? (names[0] ?? "")
    : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

/**
 * Who likes a place, in words: "Liked by you and Ana", or "Nobody has liked it yet". The face
 * stack beside it is a picture of this sentence, so the sentence is what a screen reader gets.
 * Without the names (a list cached before they were sent) it falls back to the count. Never more
 * than a few names however many like it: it sits on a row that has to stay a row.
 */
export function likedSaid(poi: Pick<Poi, "votes" | "voters">, me: string | null): string {
  const { voters, votes } = poi;
  if (!voters) {
    return votes === 0
      ? "Nobody has liked it yet"
      : `${votes} ${votes === 1 ? "person likes" : "people like"} it`;
  }
  if (voters.length === 0) return "Nobody has liked it yet";
  const names = [
    ...voters.filter((v) => v.userId === me).map(() => "you"),
    ...voters.filter((v) => v.userId !== me).map((v) => v.name),
  ];
  return `Liked by ${listSaid(names)}`;
}

/**
 * The faces of those who like a place, overlapping, the reader first: at most `MAX_FACES`, then a
 * chip saying how many more. Nothing at all when nobody does. Without `voters` (a list saved on this device) a single chip
 * with the count stands in for the faces.
 *
 * With a `label` the stack is an image with that name, and a screen reader hears who likes the
 * place; a surface that prints `likedSaid` beside the faces gives none, and the stack is then
 * decorative.
 */
export function Likers({
  voters,
  votes = 0,
  me,
  colorOf,
  label,
}: {
  voters: Poi["voters"];
  /** How many like it: shown as a count chip when `voters` is absent (a list saved on this device). */
  votes?: number | undefined;
  /** What the stack says to a screen reader: `likedSaid`, where no sentence sits beside it. */
  label?: string | undefined;
  me: string | null;
  /** A member's colour on this trip; null for someone who is no longer on it. */
  colorOf?: ((userId: string) => PaletteColor | null) | undefined;
}) {
  const labelProps =
    label === undefined ? { "aria-hidden": true as const } : { role: "img", "aria-label": label };
  if (!voters) {
    return votes > 0 ? (
      <span className="poi-faces" {...labelProps}>
        <span className="poi-faces-more">{votes}</span>
      </span>
    ) : null;
  }
  if (voters.length === 0) return null;
  const ordered = [
    ...voters.filter((v) => v.userId === me),
    ...voters.filter((v) => v.userId !== me),
  ];
  const more = ordered.length - MAX_FACES;
  return (
    <span className="poi-faces" {...labelProps}>
      {ordered.slice(0, MAX_FACES).map((v) => (
        <Avatar key={v.userId} name={v.name} color={colorOf?.(v.userId) ?? null} size="sm" />
      ))}
      {more > 0 && <span className="poi-faces-more">+{more}</span>}
    </span>
  );
}
